import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { clearConversation, clearSpeechRateAlert, addTranscript, updateSpeechRateAnalysis, addQuestion, addAnswer, addStoredAnswer } from '../store/conversationSlice';
import { logoutUser } from '../store/authSlice';
import { getAuth, signOut } from 'firebase/auth';
import TopBar from '../components/TopBar';
import ConversationFeed from '../components/ConversationFeed';
import TranscriptFeed from '../components/TranscriptFeed';
import AudioCaptureModal from '../components/AudioCaptureModal';
import { TranscriptionResult } from '../services/audioCaptureService';
import { speechRateService } from '../services/speechRateService';
import { LandmineAlertsContainer } from '../components/LandmineAlert';
import CompetitorAlert from '../components/CompetitorAlert';
import { removeLandmineAlert, addLandmineAlert } from '../store/slices/landmineSlice';
import { removeCompetitorAlert, addCompetitorAlert } from '../store/slices/competitorSlice';
import { generateClientId } from '../utils/clientId';
import { trainingWebSocketService } from '../services/trainingWebSocketService';
import { playBase64Audio } from '../utils/audioPlayer';
import { audioCaptureService } from '../services/audioCaptureService';
import { config } from '../config';

const TrainingSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const dispatch = useDispatch();

  // Use ref for immediate updates (avoids React state update delay)
  const isTrainingConnectedRef = useRef(false);

  // Get trackId from URL query params
  const location = useLocation();
  const trackId = new URLSearchParams(location.search).get('trackId');

  const { lastSpeechRateAlert, transcripts, speechRateSettings } = useSelector((state: RootState) => state.conversation);
  const { isAuthenticated, user, tokenInfo, isMongoVerified } = useSelector((state: RootState) => state.auth);

  const [showSpeechAlert, setShowSpeechAlert] = useState(false);
  const [isAudioCaptureActive, setIsAudioCaptureActive] = useState(false);
  const [clientId, setClientId] = useState<string>('');
  const [isTrainingConnected, setIsTrainingConnected] = useState(false);
  const [currentTrainingItem, setCurrentTrainingItem] = useState<any>(null);
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionResult, setCompletionResult] = useState<any>(null);

  // Manual control states
  const [sessionPhase, setSessionPhase] = useState<'waiting' | 'delivering' | 'recording' | 'evaluating' | 'completed'>('waiting');
  const [totalItems, setTotalItems] = useState(0);
  const [completedItems, setCompletedItems] = useState(0);
  const [hasSpokenForCurrentItem, setHasSpokenForCurrentItem] = useState(false);
  const [allItemsCompleted, setAllItemsCompleted] = useState(false);

  // Refs to track latest values (for callbacks that capture stale state)
  const currentTrainingItemRef = useRef<any>(null);
  const sessionPhaseRef = useRef<'waiting' | 'delivering' | 'recording' | 'evaluating' | 'completed'>('waiting');
  const totalItemsRef = useRef<number>(0);

  // Get alerts from Redux store
  const landmineAlerts = useSelector((state: RootState) => state.landmine?.alerts || []);
  const competitorAlerts = useSelector((state: RootState) => state.competitor?.alerts || []);

  // Initialize session
  useEffect(() => {
    const initializeSession = async () => {
      const newClientId = generateClientId();
      setClientId(newClientId);

      // Check if session already exists in database
      if (sessionId && isAuthenticated && tokenInfo?.token) {
        console.log('🔍 Checking if training session already exists:', sessionId);
        try {
          const { apiService } = await import('../services/apiService');
          // Add timestamp to prevent caching
          const timestamp = Date.now();
          const response = await apiService.get(`/training/sessions/check/${sessionId}?_t=${timestamp}`);

          console.log('✅ Session check response:', response);

          // Check if exists field is true (it's at top level, not in data)
          if (response.success && (response as any).exists === true) {
            // Session already exists - show alert and logout
            console.warn('⚠️ Session already exists - showing alert and logging out');
            window.alert('This training session has already ended. Please start a new training session from the admin portal.');

            // Logout user
            const auth = getAuth();
            await signOut(auth);
            dispatch(logoutUser() as any);
            navigate('/login');
            return;
          } else {
            console.log('✅ Session does not exist - continuing with initialization');
          }
        } catch (error) {
          console.error('❌ Error checking session existence:', error);
          // Continue anyway if API check fails (better to allow than block incorrectly)
        }
      } else {
        console.log('⏭️ Skipping session check - missing requirements:', {
          hasSessionId: !!sessionId,
          isAuthenticated,
          hasToken: !!tokenInfo?.token
        });
      }

      // Initialize session manager with training sessionId as room_id
      // This ensures Deepgram proxy uses the training sessionId instead of 'default'
      if (sessionId) {
        import('../services/sessionManager').then(({ sessionManager }) => {
          sessionManager.forceNewSession(sessionId);
        });
      }
    };

    initializeSession();
  }, [sessionId, isAuthenticated, tokenInfo, dispatch, navigate]);

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated || !tokenInfo?.token) {
      console.log('🔐 User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }

    if (!isMongoVerified) {
      console.log('🔍 User not verified in MongoDB, redirecting to login');
      navigate('/login');
      return;
    }
  }, [isAuthenticated, tokenInfo, isMongoVerified, navigate]);

  // Cleanup on dirty close (user closes tab/window)
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log('⚠️ Page unloading - cleaning up training session...');

      // Stop audio capture and close Deepgram connections
      if (isAudioCaptureActive) {
        audioCaptureService.stopCapture();
      }

      // Disconnect from training service
      if (isTrainingConnected) {
        trainingWebSocketService.disconnect();
      }

      // Note: We don't show a confirmation dialog for training sessions
      // as the backend will handle cleanup automatically
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAudioCaptureActive, isTrainingConnected]);

  // Setup event handlers once on mount
  useEffect(() => {
    // Set up event handlers
    trainingWebSocketService.onSessionStarted((data) => {
      console.log('🎓 Session started:', data);
      // Mark as connected when session actually starts (update ref immediately)
      isTrainingConnectedRef.current = true;
      setIsTrainingConnected(true);
      const itemCount = data.itemCount || 0;
      setTotalItems(itemCount);
      totalItemsRef.current = itemCount; // Update ref for callbacks
      setCompletedItems(0);
      setAllItemsCompleted(false); // Reset flag for new session
      setSessionPhase('waiting'); // Waiting for user to click "Deliver 1st Item"
      sessionPhaseRef.current = 'waiting';
    });

    trainingWebSocketService.onItemDelivered(async (item) => {
      console.log('🎓 New training item:', item);
      console.log('📊 Phase transition: → delivering (new item received)');
      setSessionPhase('delivering'); // AI is delivering the item
      sessionPhaseRef.current = 'delivering';
      setHasSpokenForCurrentItem(false); // Reset for new item

      // Add AI customer transcript to transcript feed ONLY
      if (item.aiText) {
        dispatch(addTranscript({
          text: item.aiText,
          speaker: 'customer',
          timestamp: new Date().toISOString()
        }));

        // If it's an objection or FAQ, add to ConversationFeed WITH both stored and AI answers
        if (item.itemType === 'objection' || item.itemType === 'faq' || item.itemType === 'question') {
          const questionPayload = {
            category: item.itemType === 'faq' ? 'FAQ' : 'Objection',
            question: item.aiText,
            relevance_score: 1.0,
            timestamp: new Date().toISOString()
          };
          dispatch(addQuestion(questionPayload));

          // Add stored database answer immediately (if available)
          // This matches the live call pattern: stored_answer_generated event → addStoredAnswer action
          if (item.storedAnswer) {
            dispatch(addStoredAnswer({
              question: item.aiText,
              stored_answer: item.storedAnswer,
              timestamp: new Date().toISOString()
            }));
          }

          // Add AI-generated answer immediately (generated in parallel with TTS)
          // This matches the live call pattern: answer_generated event → addAnswer action
          if (item.aiGeneratedAnswer) {
            dispatch(addAnswer({
              question: item.aiText,
              answer: item.aiGeneratedAnswer,
              timestamp: new Date().toISOString()
            }));
          }
        }

        // Handle detected objections from AI customer text (landmines/competitors)
        if (item.detectedObjections && item.detectedObjections.length > 0) {
          item.detectedObjections.forEach((objection) => {
            if (objection.type === 'landmine') {
              // Customer is asking about a landmine topic
              dispatch(addLandmineAlert({
                trigger: objection.trigger || 'Landmine Topic',
                matched_text: item.aiText,
                matched_phrase: objection.trigger || 'Landmine Topic',
                similarity_score: 1.0,  // Training mode - direct match
                semantic_match: false,  // Training mode - not from vector search
                landmine_type: objection.landmine_type || 'legal_risk',
                category: objection.category || 'Customer Landmine Question',
                priority: 'high',
                can_say: objection.can_say || [],
                cannot_say: objection.cannot_say || [],
                quick_tips: objection.quick_tips || 'Use the ideal answer provided below as guidance',
                detected_at: new Date().toISOString(),
                timestamp: new Date().toISOString(),
                speaker: 'customer'
              }));
            } else if (objection.type === 'competitor') {
              // Customer mentioned a competitor
              dispatch(addCompetitorAlert({
                competitor: objection.competitor || 'Competitor',
                matched_text: item.aiText,
                confidence: 1.0,
                match_type: 'customer_mention',
                advantages: objection.advantages || [],
                weaknesses: objection.weaknesses || [],
                talking_points: objection.talking_points || 'Focus on our unique value and differentiation',
                category: objection.category || 'Customer Mentioned Competitor',
                detected_at: new Date().toISOString(),
                timestamp: new Date().toISOString(),
                speaker: 'customer'
              }));
            }
          });
        }
      }

      // Play AI customer audio if available
      if (item.aiAudioBase64) {
        try {
          await playBase64Audio(item.aiAudioBase64);
        } catch (error) {
          console.error('❌ Error playing AI audio:', error);
        }
      }

      // Set current item with timestamp after audio finishes
      const newItem = {
        ...item,
        deliveredAt: Date.now()
      };
      setCurrentTrainingItem(newItem);
      currentTrainingItemRef.current = newItem; // Update ref for callbacks

      // After AI audio plays, change to recording phase
      console.log('📊 Phase transition: delivering → recording (AI audio finished)');
      setSessionPhase('recording'); // User can now record their response
      sessionPhaseRef.current = 'recording'; // Update ref for callbacks
    });

    trainingWebSocketService.onItemEvaluated(async (evaluation) => {
      console.log('✅ Item evaluated:', evaluation);
      const newCompletedCount = (completedItems || 0) + 1;
      setCompletedItems(newCompletedCount);
      setHasSpokenForCurrentItem(false); // Reset for next item

      console.log('📊 Phase transition: evaluating → waiting (evaluation complete)');
      setSessionPhase('waiting'); // Waiting for user to click "Next Item" or for all items signal
      sessionPhaseRef.current = 'waiting';

      // Note: Ideal answer was already added when item was delivered
      // The evaluation contains feedback on the salesperson's actual response

      // Handle landmines/competitors detected in SALESPERSON's response - show as alert badges
      if (evaluation.detectedObjections && evaluation.detectedObjections.length > 0) {

        evaluation.detectedObjections.forEach((objection) => {
          if (objection.type === 'landmine') {
            // Salesperson triggered a landmine - show alert badge
            dispatch(addLandmineAlert({
              trigger: objection.trigger || 'Landmine Triggered',
              matched_text: `You said something that triggered: ${objection.trigger}`,
              matched_phrase: objection.trigger || 'Landmine Triggered',
              similarity_score: 1.0,  // Training mode - direct match
              semantic_match: false,  // Training mode - not from vector search
              landmine_type: objection.landmine_type || 'legal_risk',
              category: objection.category || 'Salesperson Landmine',
              priority: 'high',
              can_say: objection.can_say || [],
              cannot_say: objection.cannot_say || [],
              quick_tips: objection.quick_tips || 'Rephrase without making guarantees or promises',
              detected_at: new Date().toISOString(),
              timestamp: new Date().toISOString(),
              speaker: 'salesperson'
            }));
          } else if (objection.type === 'competitor') {
            // Salesperson mentioned a competitor - show alert badge
            console.log('🎯 Competitor mentioned by salesperson:', {
              competitor: objection.competitor,
              advantages: objection.advantages,
              weaknesses: objection.weaknesses,
              talking_points: objection.talking_points
            });
            dispatch(addCompetitorAlert({
              competitor: objection.competitor || 'Competitor',
              matched_text: `You mentioned: ${objection.competitor}`,
              confidence: 1.0,
              match_type: 'salesperson_mention',
              advantages: objection.advantages || [],
              weaknesses: objection.weaknesses || [],
              talking_points: objection.talking_points || 'Redirect conversation to our strengths instead of mentioning competitors',
              category: objection.category || 'Salesperson Mentioned Competitor',
              detected_at: new Date().toISOString(),
              timestamp: new Date().toISOString(),
              speaker: 'salesperson'
            }));
          }
        });
      }

      // Add AI follow-up transcript to feed if available
      if (evaluation.aiFollowUp) {
        dispatch(addTranscript({
          text: evaluation.aiFollowUp,
          speaker: 'customer',
          timestamp: new Date().toISOString()
        }));
      }

      // Play AI follow-up audio if available
      if (evaluation.aiFollowUpAudioBase64) {
        try {
          await playBase64Audio(evaluation.aiFollowUpAudioBase64);
        } catch (error) {
          console.error('❌ Error playing AI follow-up audio:', error);
        }
      }

      // Microphone stays active for next response (unless this was the last item)
    });

    trainingWebSocketService.onAllItemsDelivered(() => {
      console.log('🎓 All items delivered signal received from backend');
      setAllItemsCompleted(true);

      // Force completedItems to match totalItems for accurate display
      // Use ref to get current value (not stale closure value)
      setCompletedItems(totalItemsRef.current);

      // Clear current item display
      setCurrentTrainingItem(null);
      currentTrainingItemRef.current = null;

      console.log(`📊 All items complete (${totalItemsRef.current}/${totalItemsRef.current}) - ready for user to finish session`);
    });

    trainingWebSocketService.onSessionCompleted((result) => {
      console.log('🎓 Session completed:', result);
      setIsAudioCaptureActive(false);

      // Stop audio capture completely
      audioCaptureService.stopCapture();

      // Show completion modal with results
      setCompletionResult(result);
      setShowCompletionModal(true);
    });

    trainingWebSocketService.onError((error) => {
      console.error('🎓 Training error:', error);
    });

    trainingWebSocketService.onConnectionChange((connected) => {
      setIsTrainingConnected(connected);
    });

    return () => {
      console.log('🎓 Disconnecting from training simulation service');
      trainingWebSocketService.disconnect();
    };
  }, [navigate]);

  // Note: Training WebSocket connection is initiated in handleAudioModalOpen
  // Note: Deepgram connection is handled by AudioCaptureModal component
  // The modal uses audioCaptureService which connects to deepgram_proxy_service.py
  // Transcriptions are received via handleTranscription callback

  // Speech rate monitoring
  useEffect(() => {
    if (speechRateSettings.enabled && isAudioCaptureActive && isAuthenticated && tokenInfo?.token) {
      speechRateService.startMonitoring(
        () => transcripts,
        () => ({
          time_window: speechRateSettings.time_window,
          max_words: speechRateSettings.max_words
        }),
        (analysis) => {
          dispatch(updateSpeechRateAnalysis(analysis));
        },
        2000
      );

      return () => {
        speechRateService.stopMonitoring();
      };
    }
  }, [speechRateSettings.enabled, speechRateSettings.time_window, speechRateSettings.max_words, transcripts, dispatch, isAudioCaptureActive, isAuthenticated, tokenInfo]);

  // Handle speech rate alerts
  useEffect(() => {
    if (lastSpeechRateAlert) {
      setShowSpeechAlert(true);

      const timer = setTimeout(() => {
        setShowSpeechAlert(false);
        dispatch(clearSpeechRateAlert());
      }, 7000);

      return () => clearTimeout(timer);
    }
  }, [lastSpeechRateAlert, dispatch]);

  // Alert dismiss functions
  const dismissLandmineAlert = (index: number) => {
    dispatch(removeLandmineAlert(index));
  };

  const dismissCompetitorAlert = (index: number) => {
    dispatch(removeCompetitorAlert(index));
  };

  const handleSignOut = async () => {
    try {
      dispatch(clearConversation());

      const auth = getAuth();
      await signOut(auth);

      dispatch(logoutUser() as any);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      navigate('/login');
    }
  };

  const dismissSpeechAlert = (e?: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent any default browser behavior and event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowSpeechAlert(false);
    dispatch(clearSpeechRateAlert());
  };

  const handleCaptureStateChange = (isCapturing: boolean) => {
    setIsAudioCaptureActive(isCapturing);

    // When audio capture stops, disconnect from training service
    if (!isCapturing && isTrainingConnected) {
      console.log('🛑 Audio capture stopped - disconnecting from training service');
      trainingWebSocketService.disconnect();
      isTrainingConnectedRef.current = false;
      setIsTrainingConnected(false);
      setCurrentTrainingItem(null);
      currentTrainingItemRef.current = null;
    }
  };

  const handleAudioModalClose = () => {
    // Allow closing the modal without stopping audio capture
    setIsAudioModalOpen(false);
  };

  const handleAudioModalOpen = () => {
    setIsAudioModalOpen(true);
  };

  const handleStartTrainingConnection = () => {
    // Start training WebSocket connection when user clicks "Start Recording"
    if (!isTrainingConnected && trackId && isAuthenticated && user?.uid) {
      console.log('🔌 Initiating training WebSocket connection...');
      console.log('   Training URL:', config.server.trainingUrl);
      console.log('   Track ID:', trackId);
      console.log('   Session ID:', sessionId);
      console.log('   User ID:', user.uid);

      trainingWebSocketService.connect(trackId, sessionId)
        .then(() => {
          console.log('✅ WebSocket connected to training service');
          isTrainingConnectedRef.current = true;
          setIsTrainingConnected(true);
        })
        .catch((error) => {
          console.error('❌ Failed to connect to training service:', error);
          isTrainingConnectedRef.current = false;
          setIsTrainingConnected(false);
        });
    }
  };

  const handleTranscription = (result: TranscriptionResult) => {
    // Only log final transcripts to reduce noise
    if (result.isFinal) {
      console.log('🔍 handleTranscription (final):', {
        transcript: result.transcript,
        speaker: result.speaker,
        sessionPhase: sessionPhaseRef.current,
        hasSpokenForCurrentItem,
        currentTrainingItem: !!currentTrainingItemRef.current
      });
    }

    if (!isAuthenticated || !tokenInfo?.token) {
      console.warn('🔐 Cannot process transcription - user not authenticated');
      return;
    }

    // Add transcript to Redux store
    dispatch(addTranscript({
      text: result.transcript,
      speaker: result.speaker || 'salesperson',
      timestamp: result.timestamp.toISOString()
    }));

    // Track that user has spoken for current item (for button enable/disable)
    // Only enable button if there's a current training item (i.e., after first item is delivered)
    console.log('🔍 Checking button enable condition:', {
      isFinal: result.isFinal,
      speaker: result.speaker,
      speakerMatch: result.speaker === 'salesperson',
      hasCurrentItem: !!currentTrainingItemRef.current,
      sessionPhase: sessionPhaseRef.current,
      willEnable: result.isFinal && result.speaker === 'salesperson' && currentTrainingItemRef.current !== null
    });

    if (result.isFinal && result.speaker === 'salesperson' && currentTrainingItemRef.current !== null) {
      console.log('✅ User has spoken - enabling Submit button', { sessionPhase: sessionPhaseRef.current, transcript: result.transcript });
      setHasSpokenForCurrentItem(true);
      // Ensure we're in recording phase if user has spoken during delivery
      if (sessionPhaseRef.current === 'delivering') {
        console.log('📊 Phase transition: delivering → recording (user started speaking)');
        setSessionPhase('recording');
        sessionPhaseRef.current = 'recording';
      }
    }

    // Send real-time transcripts to training service (for collection, not evaluation)
    if (result.isFinal && result.speaker === 'salesperson' && isTrainingConnectedRef.current) {
      trainingWebSocketService.submitResponse(
        currentTrainingItemRef.current?.trackItemId || '',
        result.transcript,
        0 // Response time will be calculated when user clicks "Next"
      );
    }
  };

  // Manual control handlers
  const handleDeliverFirstItem = () => {
    console.log('🎓 User clicked: Deliver 1st Item');
    console.log('📊 Phase transition: waiting → delivering');
    setSessionPhase('delivering');
    sessionPhaseRef.current = 'delivering';
    trainingWebSocketService.sendMessage({ type: 'deliver_first_item', data: {} });
  };

  const handleSubmitAndNext = () => {
    console.log('📊 Submit button state:', { hasSpokenForCurrentItem, sessionPhase, completedItems, totalItems });
    if (!hasSpokenForCurrentItem) {
      alert('Please speak your response before clicking Next.');
      return;
    }

    console.log('🎓 User clicked: Submit & Next Item');
    console.log('📊 Phase transition: recording → evaluating');
    setSessionPhase('evaluating');
    sessionPhaseRef.current = 'evaluating';
    setHasSpokenForCurrentItem(false);
    trainingWebSocketService.sendMessage({ type: 'submit_and_next', data: {} });
  };

  const handleFinishSession = () => {
    console.log('🎓 User clicked: Finish Session');
    setSessionPhase('completed');
    sessionPhaseRef.current = 'completed';
    trainingWebSocketService.sendMessage({ type: 'finish_session', data: {} });
  };

  // Don't render if not authenticated
  if (!isAuthenticated || !tokenInfo?.token || !isMongoVerified) {
    return null;
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Fixed Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <TopBar onSignOut={handleSignOut} clientId={clientId} />

        {/* Speech Rate Alert Banner */}
        {showSpeechAlert && lastSpeechRateAlert && (
          <div className="bg-red-500 text-white px-6 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-xl">⚠️</span>
              <div>
                <span className="font-bold">SPEAKING TOO FAST!</span>
                <span className="ml-2">
                  {lastSpeechRateAlert.total_words}/{lastSpeechRateAlert.max_words} words
                  in {lastSpeechRateAlert.time_window}s - Please slow down
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={dismissSpeechAlert}
              className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm transition-colors"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* Landmine Alerts */}
      <LandmineAlertsContainer
        alerts={landmineAlerts}
        onDismiss={(index) => dismissLandmineAlert(index)}
      />

      {/* Competitor Alerts */}
      <div className="fixed top-32 right-4 z-40 space-y-2">
        {competitorAlerts.map((alert: any, index: number) => (
          <CompetitorAlert
            key={`competitor-alert-${index}`}
            alert={alert}
            onDismiss={() => dismissCompetitorAlert(index)}
          />
        ))}
      </div>

      {/* Main Content Area */}
      <div
        className="flex-1 flex min-h-0 gap-4 px-4"
        style={{
          marginTop: showSpeechAlert ? '96px' : '48px',
          marginBottom: '48px'
        }}
      >
        {/* Left side - Training Info & Live Transcripts (40%) */}
        <div className="w-2/5 flex flex-col min-h-0 space-y-4">
          {/* Training Session Info */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900">Training Session</h3>
              <div className="flex flex-col space-y-1">
                {/* Recording Status */}
                {isAudioCaptureActive && (
                  <span className="px-3 py-1 text-xs rounded-full text-center bg-red-100 text-red-800 animate-pulse">
                    🔴 Recording
                  </span>
                )}
                {/* Training Service Status */}
                <span className={`px-3 py-1 text-xs rounded-full text-center ${
                  isTrainingConnected ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {isTrainingConnected ? '✅ Training Service Connected' : '⏳ Waiting for audio...'}
                </span>
                {!isTrainingConnected && !isAudioCaptureActive && (
                  <span className="text-xs text-gray-500 text-center">
                    Click "Start Audio Capture" below
                  </span>
                )}
              </div>
            </div>

            {/* Current Training Item */}
            {currentTrainingItem && (
              <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                <div className="text-sm font-semibold text-blue-900 mb-2">
                  Current Item: {currentTrainingItem.itemType}
                </div>
                <div className="text-sm text-gray-700">
                  {currentTrainingItem.aiText}
                </div>
              </div>
            )}

          </div>

          {/* Session Status Indicator - Compact */}
          {isAudioCaptureActive && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${
                    sessionPhase === 'delivering' ? 'bg-yellow-500 animate-pulse' :
                    sessionPhase === 'recording' ? 'bg-red-500 animate-pulse' :
                    sessionPhase === 'evaluating' ? 'bg-blue-500 animate-pulse' :
                    'bg-green-500'
                  }`} />
                  <div className="font-medium text-gray-900 text-sm">
                    {sessionPhase === 'waiting' && !allItemsCompleted && '⏳ Waiting'}
                    {sessionPhase === 'waiting' && allItemsCompleted && '✅ All Complete'}
                    {sessionPhase === 'delivering' && '🎤 AI Speaking'}
                    {sessionPhase === 'recording' && '🔴 Recording'}
                    {sessionPhase === 'evaluating' && '⚡ Evaluating'}
                    {sessionPhase === 'completed' && '✅ Complete'}
                  </div>
                  <span className="text-xs text-gray-600">•</span>
                  <div className="text-xs text-gray-600">
                    {completedItems}/{totalItems}
                  </div>
                </div>
                {hasSpokenForCurrentItem && sessionPhase === 'recording' && (
                  <span className="text-xs text-green-600 font-medium">✓ Ready</span>
                )}
              </div>
            </div>
          )}

          {/* Manual Control Buttons */}
          {!isAudioModalOpen && !isAudioCaptureActive && (
            <button
              onClick={handleAudioModalOpen}
              className="w-full px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white"
            >
              <span>🎙️ Start Audio Capture</span>
            </button>
          )}

          {/* Training Control Buttons - Show after audio capture is active */}
          {isAudioCaptureActive && isTrainingConnected && (
            <div className="space-y-2">
              {/* Deliver 1st Item Button - Only show before first item */}
              {completedItems === 0 && !currentTrainingItem && sessionPhase === 'waiting' && (
                <button
                  onClick={handleDeliverFirstItem}
                  className="w-full px-4 py-3 rounded-lg font-bold transition-colors bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg"
                >
                  Deliver 1st Item
                </button>
              )}

              {/* Submit & Next Item Button with Stop Early - Show when there's a current item */}
              {currentTrainingItem && !allItemsCompleted && (sessionPhase === 'recording' || sessionPhase === 'delivering') && (
                <div className="flex gap-2">
                  <button
                    onClick={handleSubmitAndNext}
                    disabled={!hasSpokenForCurrentItem}
                    className={`flex-1 px-4 py-3 rounded-lg font-bold transition-colors shadow-lg ${
                      hasSpokenForCurrentItem
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white cursor-pointer'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {completedItems < totalItems - 1 ? '➡️ Submit & Next' : '➡️ Submit'}
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to stop the training session early?')) {
                        console.log('🛑 Stopping audio capture from main button');
                        audioCaptureService.stopCapture();
                        setIsAudioCaptureActive(false);
                        if (isTrainingConnected) {
                          console.log('🛑 Disconnecting from training service');
                          trainingWebSocketService.disconnect();
                          isTrainingConnectedRef.current = false;
                          setIsTrainingConnected(false);
                          setCurrentTrainingItem(null);
                          currentTrainingItemRef.current = null;
                        }
                      }
                    }}
                    className="px-4 py-3 rounded-lg font-medium transition-colors bg-red-500 hover:bg-red-600 text-white text-sm whitespace-nowrap"
                  >
                    🛑 Stop Early
                  </button>
                </div>
              )}

              {/* Finish Session Button - Show ONLY after all items completed */}
              {allItemsCompleted && sessionPhase === 'waiting' && (
                <button
                  onClick={handleFinishSession}
                  className="w-full px-4 py-3 rounded-lg font-bold transition-colors bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white shadow-lg"
                >
                  Finish Session
                </button>
              )}

              {/* Stop Early Button - Show ONLY when NOT all items completed and in waiting/evaluating */}
              {!allItemsCompleted && sessionPhase !== 'recording' && sessionPhase !== 'delivering' && !currentTrainingItem && (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to stop the training session early?')) {
                      console.log('🛑 Stopping audio capture from main button');
                      audioCaptureService.stopCapture();
                      setIsAudioCaptureActive(false);
                      if (isTrainingConnected) {
                        console.log('🛑 Disconnecting from training service');
                        trainingWebSocketService.disconnect();
                        isTrainingConnectedRef.current = false;
                        setIsTrainingConnected(false);
                        setCurrentTrainingItem(null);
                        currentTrainingItemRef.current = null;
                      }
                    }
                  }}
                  className="w-full px-3 py-2 rounded-lg font-medium transition-colors bg-red-500 hover:bg-red-600 text-white text-sm"
                >
                  🛑 Stop Training Early
                </button>
              )}
            </div>
          )}

          <AudioCaptureModal
            isOpen={isAudioModalOpen}
            onClose={handleAudioModalClose}
            onTranscription={handleTranscription}
            onCaptureStateChange={handleCaptureStateChange}
            isTrainingConnected={isTrainingConnected}
            waitForTrainingConnection={true}
            onStartTrainingConnection={handleStartTrainingConnection}
          />

          <TranscriptFeed isCustomerSpeaking={sessionPhase === 'delivering'} />
        </div>

        {/* Right side - Training Evaluation & Feedback (60%) */}
        <div className="w-3/5 flex flex-col min-h-0">
          <ConversationFeed />
        </div>
      </div>

      {/* Fixed Status Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-2 z-40">
        <div className="text-center text-xs text-gray-500">
          Sales Guru Training - Session: {sessionId}
        </div>
      </div>

      {/* Training Completion Modal */}
      {showCompletionModal && completionResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg
                  className="h-10 w-10 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Training Session Complete! 🎉
              </h3>

              {/* Score */}
              <div className="mb-6">
                <div className="text-5xl font-bold text-blue-600 mb-2">
                  {completionResult.overallScore || 0}%
                </div>
                <p className="text-gray-600">Overall Score</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                <div className="bg-gray-50 rounded p-3">
                  <div className="font-semibold text-gray-700">Items Completed</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {completionResult.itemsCompleted || 0}
                  </div>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <div className="font-semibold text-gray-700">Total Items</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {completionResult.totalItems || 0}
                  </div>
                </div>
              </div>

              {/* Message */}
              <p className="text-gray-600 mb-6">
                Great job! Your session has been saved and is ready for review.
              </p>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    // Redirect to admin portal training session details
                    window.location.href = `${config.server.adminPortalUrl}/training`;
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  View Detailed Results
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingSessionPage;
